import {API} from '@/classes/api/api.class';
import {EventTopic} from '@/interfaces/eventTopic.interface';
import {Types} from 'mongoose';
import {EventInfo} from '@/interfaces/eventInfo.interface';
import {CreateEventTopicRequestDto} from '@/classes/dto/event-manager/request/CreateEventTopic.request.dto';
import {FindEventTopicRequestDto} from '@/classes/dto/event-manager/request/FindEventTopic.request.dto';
import {UpdateEventTopicRequestDto} from '@/classes/dto/event-manager/request/UpdateEventTopic.request.dto';
import {CreateEventInfoRequestDto} from '@/classes/dto/event-manager/request/CreateEventInfo.request.dto';
import {FindEventInfoRequestDto} from '@/classes/dto/event-manager/request/FindEventInfo.request.dto';
import {
    AddEventInfoParticipantRequestDto,
} from '@/classes/dto/event-manager/request/AddEventInfoParticipant.request.dto';
import {
    RemoveEventInfoParticipantRequestDto,
} from '@/classes/dto/event-manager/request/RemoveEventInfoParticipant.request.dto';
import {
    SetEventInfoParticipationStateRequestDto,
} from '@/classes/dto/event-manager/request/SetEventInfoParticipationState.request.dto';
import {RemindParticipantsRequestDto} from '@/classes/dto/event-manager/request/RemindParticipants.request.dto';
import {RemindParticipantsResponseDto} from '@/classes/dto/event-manager/response/RemindParticipants.response.dto';
import {PreviewParticipantMailsRequestDto} from '@/classes/dto/event-manager/request/PreviewParticipantMails.request.dto';
import {MailDraftsPreviewResponseDto} from '@/classes/dto/_common/mail-review/MailDraft.dto';
import {MailReportResponseDto} from '@/classes/dto/_common/response/mailReport.response.dto';
import {SendParticipantMailsRequestDto} from '@/classes/dto/event-manager/request/SendParticipantMails.request.dto';
import {
    EventParticipationResponse,
    UpcomingEventParticipation,
    UpcomingEventParticipationResponse,
} from '@/interfaces/eventParticipationResponse.interface';
import {UpdateEventInfoRequestDto} from '@/classes/dto/event-manager/request/UpdateEventInfo.request.dto';
import {
    SetEventInfoParticipantStatusRequestDto,
} from '@/classes/dto/event-manager/request/SetEventInfoParticipantStatus.request.dto';
import {FindByIdOptionsRequestDto} from '@/classes/dto/_common/request/FindByIdOptions.request.dto';
import {
    BulkAddEventInfoParticipantRequestDto,
} from '@/classes/dto/event-manager/request/BulkAddEventInfoParticipant.request.dto';
import {
    BulkAddEventInfoParticipantResponseDto,
} from '@/classes/dto/event-manager/response/BulkAddEventInfoParticipant.response.dto';
import {CreateEventInfoSeriesRequestDto} from '@/classes/dto/event-manager/request/CreateEventInfoSeries.request.dto';
import {
    AddEventInfoSeriesParticipantRequestDto,
} from '@/classes/dto/event-manager/request/AddEventInfoSeriesParticipant.request.dto';
import {
    RemoveEventInfoSeriesParticipantRequestDto,
} from '@/classes/dto/event-manager/request/RemoveEventInfoSeriesParticipant.request.dto';
import {UpdateEventInfoSeriesRequestDto} from '@/classes/dto/event-manager/request/UpdateEventInfoSeries.request.dto';
import {
    AddEventInfoSeriesParticipantResponseDto,
    CreateEventInfoSeriesResponseDto,
    RemoveEventInfoSeriesParticipantResponseDto,
    UpdateEventInfoSeriesResponseDto,
} from '@/classes/dto/event-manager/response/EventInfoSeries.response.dto';

export class EventManagerApi extends API {
    static service = 'event-manager';

    static async getParticipations(participantId: Types.ObjectId, options?: FindByIdOptionsRequestDto): Promise<EventParticipationResponse> {
        return this.get(`participations/${participantId}`, options);
    }

    /**
     * Upcoming trainings of a participant (newer backend). Accepts a plain array or a wrapped list as response.
     */
    static async getUpcomingParticipations(participantId: Types.ObjectId): Promise<UpcomingEventParticipation[]> {
        const resp: UpcomingEventParticipationResponse | UpcomingEventParticipation[] | Record<string, unknown> | undefined =
            await this.get(`participations/${participantId}/upcoming`);
        if (Array.isArray(resp)) {
            return resp;
        }
        if (resp && typeof resp === 'object') {
            const list = (resp as Record<string, unknown>).upcoming || (resp as Record<string, unknown>).participations
                || (resp as Record<string, unknown>).eventInfos || (resp as Record<string, unknown>).events;
            if (Array.isArray(list)) {
                return list as UpcomingEventParticipation[];
            }
        }
        return [];
    }

    static async createEventTopic(data: CreateEventTopicRequestDto): Promise<EventTopic> {
        return this.post('topic', data);
    }

    /** Sends the invitation mails through the messenger; older backends answer without a report */
    static async remindParticipants(eventId: Types.ObjectId, data: RemindParticipantsRequestDto): Promise<RemindParticipantsResponseDto | undefined> {
        return this.post(`event-info/remindParticipants/${eventId}`, data);
    }

    /** Review flow: rendered, editable drafts of the invitation mails */
    static async previewParticipantMails(eventId: Types.ObjectId, data: PreviewParticipantMailsRequestDto): Promise<MailDraftsPreviewResponseDto> {
        return this.post(`event-info/remindParticipants/${eventId}/preview`, data);
    }

    /** Review flow: sends the (edited) drafts through the messenger */
    static async sendParticipantMails(eventId: Types.ObjectId, data: SendParticipantMailsRequestDto): Promise<MailReportResponseDto> {
        return this.post(`event-info/remindParticipants/${eventId}/send`, data);
    }

    static async findEventTopics(data: FindEventTopicRequestDto): Promise<{
        eventTopics: EventTopic[];
        maxPage: number
    }> {
        return this.get('topic', data);
    }

    static async findEventTopicById(topicId: Types.ObjectId): Promise<EventTopic> {
        return this.get(`topic/${topicId}`);
    }

    static async updateEventTopic(topicId: Types.ObjectId, update: UpdateEventTopicRequestDto): Promise<void> {
        return this.put(`topic/${topicId}`, update);
    }

    static async createEventInfo(data: CreateEventInfoRequestDto): Promise<EventInfo> {
        return this.post('event-info', data);
    }

    /** @deprecated */
    static async findEventsForParticipant(participantId: Types.ObjectId): Promise<{
        organizedByUser: EventInfo[];
        visitedByUser: EventInfo[]
    }> {
        if (participantId) {
            throw new Error(`Deprecated`);
        }
        return {organizedByUser: [], visitedByUser: []};
    }

    static async findEventInfos(data: FindEventInfoRequestDto): Promise<{ eventInfos: EventInfo[]; maxPage: number }> {
        return this.get('event-info', data);
    }

    static async findEventInfoById(eventId: Types.ObjectId, options?: FindByIdOptionsRequestDto): Promise<EventInfo> {
        return this.get(`event-info/${eventId}`, options);
    }

    /** Partial update (startTime, durationInMinutes, topic, venue, isPublic, isCancelled, organizerList, title, description, capacity) */
    static async updateEventInfo(eventId: Types.ObjectId, data: UpdateEventInfoRequestDto): Promise<EventInfo | void> {
        return this.put(`event-info/${eventId}`, data);
    }

    /** Sets the status (eingeladen/bestätigt/abgesagt) of a participant, null resets it to "geplant" */
    static async setParticipantStatus(eventId: Types.ObjectId, data: SetEventInfoParticipantStatusRequestDto): Promise<void> {
        return this.put(`event-info/set-participant-status/${eventId}`, data);
    }

    static async bulkAddParticipant(eventId: Types.ObjectId, participant: BulkAddEventInfoParticipantRequestDto): Promise<BulkAddEventInfoParticipantResponseDto> {
        return this.put(`event-info/bulk/add-participant/${eventId}`, participant);
    }

    static async addParticipant(eventId: Types.ObjectId, participant: AddEventInfoParticipantRequestDto): Promise<void> {
        return this.put(`event-info/add-participant/${eventId}`, participant);
    }

    static async removeParticipant(eventId: Types.ObjectId, data: RemoveEventInfoParticipantRequestDto): Promise<void> {
        return this.put(`event-info/remove-participant/${eventId}`, data);
    }

    static async setEventParticipationState(eventId: Types.ObjectId, data: SetEventInfoParticipationStateRequestDto): Promise<void> {
        return this.put(`event-info/set-participation-state/${eventId}`, data);
    }

    static async removeEvent(eventId: Types.ObjectId): Promise<void> {
        return this.delete(`event-info/${eventId}`);
    }

    /** Newer backend: creates one training per date as linked series (404 on older backends) */
    static async createEventInfoSeries(data: CreateEventInfoSeriesRequestDto): Promise<CreateEventInfoSeriesResponseDto> {
        return this.post('event-info/series', data);
    }

    /** Newer backend: adds a participant to all (upcoming) trainings of a series */
    static async addSeriesParticipant(seriesId: Types.ObjectId | string, data: AddEventInfoSeriesParticipantRequestDto)
        : Promise<AddEventInfoSeriesParticipantResponseDto> {
        return this.put(`event-info/series/${seriesId}/add-participant`, data);
    }

    /** Newer backend: removes a participant from all (upcoming) trainings of a series */
    static async removeSeriesParticipant(seriesId: Types.ObjectId | string, data: RemoveEventInfoSeriesParticipantRequestDto)
        : Promise<RemoveEventInfoSeriesParticipantResponseDto> {
        return this.put(`event-info/series/${seriesId}/remove-participant`, data);
    }

    /** Newer backend: partial update of the shared fields (and shiftMinutes) of all (upcoming) trainings of a series */
    static async updateEventInfoSeries(seriesId: Types.ObjectId | string, data: UpdateEventInfoSeriesRequestDto)
        : Promise<UpdateEventInfoSeriesResponseDto> {
        return this.put(`event-info/series/${seriesId}`, data);
    }
}
